/**
 * Rack plant model — the rooms, racks, units and links of one site, indexed
 * for the Rack Explorer. Pure: no I/O, no DOM, so the query language and the
 * 3D scene both read the same shape and `tests/rackExplorer.test.ts` can pin it.
 */

export type RoomRole = 'mdf' | 'mpoe' | 'idf' | 'other';
export type Numbering = 'bottom_up' | 'top_down';
export type RackFace = 'front' | 'rear';
export type Confidence = 'verified' | 'deduced' | 'contested' | 'unknown' | 'vacant';
export type LinkMedium = 'fiber_mm' | 'fiber_sm' | 'fiber' | 'copper' | 'logical' | 'other';

export interface RackLinkRow {
  id: string;
  from_unit_id: string;
  to_unit_id: string | null;
  to_rack_id: string | null;
  medium: LinkMedium;
  label: string | null;
  note: string | null;
  confidence: Exclude<Confidence, 'vacant'>;
  network_id?: string | null;
}

export type NetworkKind = 'corporate' | 'guest' | 'pos' | 'surveillance' | 'voice' | 'management' | 'wan' | 'other';

export interface SiteNetworkRow {
  id: string;
  location_id: string;
  code: string;
  name: string;
  owner: string | null;
  kind: NetworkKind;
  /** Semantic token name, e.g. 'info' → var(--info). */
  color_token: string;
  description: string | null;
  source: string | null;
  sort_order: number;
}

export interface NetworkMemberRow {
  id: string;
  network_id: string;
  unit_id: string | null;
  room_id: string | null;
  role: string | null;
  confidence: Exclude<Confidence, 'vacant'>;
  note: string | null;
}

export interface RackUnitRow {
  id: string;
  rack_id: string;
  asset_id: string | null;
  face: RackFace;
  ru_start: number;
  ru_height: number;
  label: string;
  detail: string | null;
  kind: string;
  confidence: Confidence;
  ip_address: string | null;
  /** Surveyed MAC, else the linked asset's (resolved in useRackPlant). */
  mac_address?: string | null;
  owner: string | null;
  check_note: string | null;
}

export interface RackRow {
  id: string;
  room_id: string;
  code: string;
  name: string;
  height_u: number;
  height_captured: boolean;
  numbering: Numbering;
  note: string | null;
  sort_order: number;
}

export interface RoomRow {
  id: string;
  location_id: string;
  code: string;
  name: string | null;
  role: RoomRole;
  floor: string | null;
  description: string | null;
  note: string | null;
  data_as_of: string | null;
  source: string | null;
  sort_order: number;
}

export interface RackPlantRows {
  rooms: RoomRow[];
  racks: RackRow[];
  units: RackUnitRow[];
  links: RackLinkRow[];
  networks?: SiteNetworkRow[];
  members?: NetworkMemberRow[];
}

/** A link seen from one end: `other*` is the far side. */
export interface LinkEnd {
  link: RackLinkRow;
  otherRackId: string;
  otherUnitId: string | null;
}

export interface RackPlant {
  rooms: RoomRow[];
  racks: RackRow[];
  units: RackUnitRow[];
  links: RackLinkRow[];
  roomById: Map<string, RoomRow>;
  rackById: Map<string, RackRow>;
  unitById: Map<string, RackUnitRow>;
  racksByRoom: Map<string, RackRow[]>;
  unitsByRack: Map<string, RackUnitRow[]>;
  /** unit id → links touching it (either end). */
  linksByUnit: Map<string, LinkEnd[]>;
  /** rack id → links with an end in it, seen from that rack. */
  linksByRack: Map<string, LinkEnd[]>;
  networks: SiteNetworkRow[];
  networkById: Map<string, SiteNetworkRow>;
  membersByNetwork: Map<string, NetworkMemberRow[]>;
  /** unit id → network memberships of that unit. */
  networksByUnit: Map<string, NetworkMemberRow[]>;
}

const bySort = <T extends { sort_order: number; code: string }>(a: T, b: T) =>
  a.sort_order - b.sort_order || a.code.localeCompare(b.code);

export function buildRackPlant(rows: RackPlantRows): RackPlant {
  const rooms = [...rows.rooms].sort(bySort);
  const roomOrder = new Map(rooms.map((r, i) => [r.id, i]));
  const racks = [...rows.racks].sort(
    (a, b) => (roomOrder.get(a.room_id) ?? 0) - (roomOrder.get(b.room_id) ?? 0) || bySort(a, b),
  );
  const roomById = new Map(rooms.map((r) => [r.id, r]));
  const rackById = new Map(racks.map((r) => [r.id, r]));
  const unitById = new Map(rows.units.map((u) => [u.id, u]));

  const racksByRoom = new Map<string, RackRow[]>();
  for (const rack of racks) push(racksByRoom, rack.room_id, rack);

  const unitsByRack = new Map<string, RackUnitRow[]>();
  for (const unit of rows.units) push(unitsByRack, unit.rack_id, unit);
  for (const [rackId, list] of unitsByRack) {
    const rack = rackById.get(rackId);
    list.sort((a, b) => positionOrder(rack, a) - positionOrder(rack, b));
  }

  const linksByUnit = new Map<string, LinkEnd[]>();
  const linksByRack = new Map<string, LinkEnd[]>();
  const links: RackLinkRow[] = [];
  for (const link of rows.links) {
    const from = unitById.get(link.from_unit_id);
    const toUnit = link.to_unit_id ? unitById.get(link.to_unit_id) : undefined;
    const toRackId = toUnit?.rack_id ?? link.to_rack_id;
    if (!from || !toRackId || !rackById.has(toRackId)) continue; // dangling: outside this site
    links.push(link);
    push(linksByUnit, from.id, { link, otherRackId: toRackId, otherUnitId: toUnit?.id ?? null });
    push(linksByRack, from.rack_id, { link, otherRackId: toRackId, otherUnitId: toUnit?.id ?? null });
    if (toUnit) push(linksByUnit, toUnit.id, { link, otherRackId: from.rack_id, otherUnitId: from.id });
    if (toRackId !== from.rack_id) {
      push(linksByRack, toRackId, { link, otherRackId: from.rack_id, otherUnitId: from.id });
    }
  }

  const networks = [...(rows.networks ?? [])].sort(bySort);
  const networkById = new Map(networks.map((n) => [n.id, n]));
  const membersByNetwork = new Map<string, NetworkMemberRow[]>();
  const networksByUnit = new Map<string, NetworkMemberRow[]>();
  for (const m of rows.members ?? []) {
    if (!networkById.has(m.network_id)) continue;
    if (m.unit_id && !unitById.has(m.unit_id)) continue;
    if (m.room_id && !roomById.has(m.room_id)) continue;
    push(membersByNetwork, m.network_id, m);
    if (m.unit_id) push(networksByUnit, m.unit_id, m);
  }

  return {
    rooms, racks, units: rows.units, links, roomById, rackById, unitById, racksByRoom, unitsByRack, linksByUnit, linksByRack,
    networks, networkById, membersByNetwork, networksByUnit,
  };
}

/** A network's member is "in service" unless its membership is unknown. */
const inService = (m: NetworkMemberRow) => m.confidence !== 'unknown';

export interface NetworkRoomPresence {
  roomId: string;
  /** Devices in this room attributed to the network (in service). */
  units: RackUnitRow[];
  /** Attributed but not confirmed in service (confidence unknown). */
  unconfirmed: RackUnitRow[];
  /** The network is recorded as present here with no attributed device. */
  presenceOnly: NetworkMemberRow | null;
}

export interface NetworkFootprint {
  network: SiteNetworkRow;
  /** Rooms the network is in, in plant order. Rooms holding only unconfirmed devices are listed after. */
  rooms: NetworkRoomPresence[];
  unitIds: Set<string>;
  roomIds: Set<string>;
  /** Links tagged with the network, or joining two of its in-service devices. */
  links: RackLinkRow[];
}

/**
 * Where a network lives and how it is joined up. A room counts as "in" the
 * network when it holds an in-service member device or a room-level presence;
 * a room with only unconfirmed devices is reported separately so it is never
 * mistaken for a confirmed location.
 */
export function networkFootprint(plant: RackPlant, networkId: string): NetworkFootprint | null {
  const network = plant.networkById.get(networkId);
  if (!network) return null;
  const members = plant.membersByNetwork.get(networkId) ?? [];
  const byRoom = new Map<string, NetworkRoomPresence>();
  const entry = (roomId: string) => {
    let e = byRoom.get(roomId);
    if (!e) byRoom.set(roomId, (e = { roomId, units: [], unconfirmed: [], presenceOnly: null }));
    return e;
  };
  const unitIds = new Set<string>();
  for (const m of members) {
    if (m.room_id) {
      entry(m.room_id).presenceOnly = m;
      continue;
    }
    const unit = plant.unitById.get(m.unit_id!)!;
    const roomId = plant.rackById.get(unit.rack_id)?.room_id;
    if (!roomId) continue;
    if (inService(m)) {
      entry(roomId).units.push(unit);
      unitIds.add(unit.id);
    } else {
      entry(roomId).unconfirmed.push(unit);
    }
  }
  const order = new Map(plant.rooms.map((r, i) => [r.id, i]));
  const confirmed = (e: NetworkRoomPresence) => e.units.length > 0 || e.presenceOnly !== null;
  const rooms = [...byRoom.values()].sort(
    (a, b) => Number(confirmed(b)) - Number(confirmed(a)) || (order.get(a.roomId) ?? 0) - (order.get(b.roomId) ?? 0),
  );
  const links = plant.links.filter(
    (l) => l.network_id === networkId || (unitIds.has(l.from_unit_id) && !!l.to_unit_id && unitIds.has(l.to_unit_id)),
  );
  return {
    network,
    rooms,
    unitIds,
    roomIds: new Set(rooms.filter(confirmed).map((r) => r.roomId)),
    links,
  };
}

/** CSS colour for a network's token. */
export function networkColor(network: Pick<SiteNetworkRow, 'color_token'>): string {
  return `var(--${network.color_token})`;
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V) {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

/** Top of the rack first, front before rear — the order a person reads a face. */
function positionOrder(rack: RackRow | undefined, u: RackUnitRow): number {
  const faceOffset = u.face === 'rear' ? 1000 : 0;
  if (!rack) return faceOffset + u.ru_start;
  return faceOffset + (rack.numbering === 'bottom_up' ? rack.height_u - u.ru_start : u.ru_start);
}

/** "U41–42" / "U28" / "U4–5 rear". */
export function ruLabel(unit: Pick<RackUnitRow, 'ru_start' | 'ru_height' | 'face'>): string {
  const end = unit.ru_start + unit.ru_height - 1;
  const base = end > unit.ru_start ? `U${unit.ru_start}–${end}` : `U${unit.ru_start}`;
  return unit.face === 'rear' ? `${base} rear` : base;
}

/**
 * Offset of a unit's bottom edge from the rack floor, in RU. Rooms that count
 * from the top store U1 as the highest slot, so it is flipped here and nowhere
 * else.
 */
export function unitBottomOffset(rack: Pick<RackRow, 'height_u' | 'numbering'>, unit: Pick<RackUnitRow, 'ru_start' | 'ru_height'>): number {
  if (rack.numbering === 'bottom_up') return unit.ru_start - 1;
  return rack.height_u - (unit.ru_start + unit.ru_height - 1);
}

/** "MDF · Rack One (RR1)" — rack names already carry the room for IDFs. */
export function rackTitle(plant: RackPlant, rackId: string): string {
  const rack = plant.rackById.get(rackId);
  if (!rack) return 'Unknown rack';
  const room = plant.roomById.get(rack.room_id);
  const name = rack.name.includes(rack.code) ? rack.name : `${rack.name} (${rack.code})`;
  return room && !rack.name.includes(room.code) ? `${room.code} · ${name}` : name;
}

export function unitTitle(plant: RackPlant, unitId: string): string {
  const unit = plant.unitById.get(unitId);
  if (!unit) return 'Unknown unit';
  return `${rackTitle(plant, unit.rack_id)} · ${ruLabel(unit)} · ${unit.label}`;
}

export function roomOfRack(plant: RackPlant, rackId: string): RoomRow | undefined {
  const rack = plant.rackById.get(rackId);
  return rack ? plant.roomById.get(rack.room_id) : undefined;
}

/** Racks directly linked to this one, nearest-first by number of links. */
export function neighbourRacks(plant: RackPlant, rackId: string): { rackId: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const end of plant.linksByRack.get(rackId) ?? []) {
    if (end.otherRackId === rackId) continue;
    counts.set(end.otherRackId, (counts.get(end.otherRackId) ?? 0) + 1);
  }
  return [...counts].map(([id, count]) => ({ rackId: id, count })).sort((a, b) => b.count - a.count);
}

/**
 * Shortest rack-to-rack path over recorded links (breadth-first). Returns the
 * rack ids in order and the link used for each hop, or null when the survey
 * has no connection between them.
 */
export function rackPath(plant: RackPlant, fromRackId: string, toRackId: string): { racks: string[]; links: RackLinkRow[] } | null {
  if (fromRackId === toRackId) return { racks: [fromRackId], links: [] };
  const prev = new Map<string, { rack: string; link: RackLinkRow }>();
  const seen = new Set([fromRackId]);
  const queue = [fromRackId];
  while (queue.length) {
    const current = queue.shift()!;
    for (const end of plant.linksByRack.get(current) ?? []) {
      if (seen.has(end.otherRackId)) continue;
      seen.add(end.otherRackId);
      prev.set(end.otherRackId, { rack: current, link: end.link });
      if (end.otherRackId === toRackId) {
        const racks = [toRackId];
        const links: RackLinkRow[] = [];
        let step = prev.get(toRackId);
        while (step) {
          racks.unshift(step.rack);
          links.unshift(step.link);
          step = prev.get(step.rack);
        }
        return { racks, links };
      }
      queue.push(end.otherRackId);
    }
  }
  return null;
}

export function checkItems(plant: RackPlant): RackUnitRow[] {
  return plant.racks.flatMap((rack) => (plant.unitsByRack.get(rack.id) ?? []).filter((u) => u.check_note));
}

export interface RackInventoryRack {
  rack: RackRow;
  /** Positions shown, in rack order (top to bottom); vacant slots are left out. */
  units: RackUnitRow[];
  /** Devices in the rack before any filter, so a filtered list can say "2 of 14". */
  total: number;
}

export interface RackInventoryRoom {
  room: RoomRow;
  racks: RackInventoryRack[];
}

/**
 * Every rack's devices, grouped by room, for the full-screen panel. `text`
 * matches label, detail, IP or owner; `networkId` keeps only devices that are
 * members of that network (unconfirmed members included, so nothing silently
 * disappears). A rack with no match is dropped only while a filter is active.
 */
export function rackInventory(
  plant: RackPlant,
  filter: { text?: string; networkId?: string | null } = {},
): RackInventoryRoom[] {
  const q = filter.text?.trim().toLowerCase() ?? '';
  const filtering = q.length > 0 || !!filter.networkId;
  const matches = (u: RackUnitRow) =>
    (!q || [u.label, u.detail, u.ip_address, u.owner].some((v) => v?.toLowerCase().includes(q))) &&
    (!filter.networkId || (plant.networksByUnit.get(u.id) ?? []).some((m) => m.network_id === filter.networkId));

  return plant.rooms
    .map((room) => ({
      room,
      racks: (plant.racksByRoom.get(room.id) ?? [])
        .map((rack) => {
          const devices = (plant.unitsByRack.get(rack.id) ?? []).filter((u) => u.kind !== 'vacant');
          return { rack, units: devices.filter(matches), total: devices.length };
        })
        .filter((r) => !filtering || r.units.length > 0),
    }))
    .filter((r) => r.racks.length > 0);
}

export interface DeviceCallout {
  unitId: string;
  title: string;
  ip: string | null;
  mac: string | null;
  /** "MDF · Main distribution · DR2 · U37" — room, rack and position. */
  where: string;
  /** What the device is for (the survey's detail), if recorded. */
  role: string | null;
  confidence: Confidence;
}

/**
 * What a floating label says about one device: name, IP, MAC and where it
 * lives. A fact nobody recorded stays null and the label leaves that line out;
 * nothing is invented or placeheld.
 */
export function deviceCallout(plant: RackPlant, unitId: string): DeviceCallout | null {
  const unit = plant.unitById.get(unitId);
  if (!unit) return null;
  const rack = plant.rackById.get(unit.rack_id);
  const room = rack ? plant.roomById.get(rack.room_id) : undefined;
  const roomPart = room ? (room.name ? `${room.code} · ${room.name}` : room.code) : null;
  return {
    unitId,
    title: unit.label,
    ip: unit.ip_address?.replace(/\/32$/, '') || null,
    mac: unit.mac_address?.toLowerCase() || null,
    where: [roomPart, rack?.code, ruLabel(unit)].filter(Boolean).join(' · '),
    role: unit.detail?.trim() || null,
    confidence: unit.confidence,
  };
}

/** Units a network's callouts label: its in-service devices, in plant order. */
export function networkCalloutUnits(plant: RackPlant, networkId: string): string[] {
  const fp = networkFootprint(plant, networkId);
  if (!fp) return [];
  return plant.racks.flatMap((r) => (plant.unitsByRack.get(r.id) ?? []).filter((u) => fp.unitIds.has(u.id)).map((u) => u.id));
}

export interface RoomNetworkPresence {
  network: SiteNetworkRow;
  /** How the network is placed in the room: a room-level presence, or its in-service devices there. */
  via: 'presence' | 'devices';
  confidence: Exclude<Confidence, 'vacant'>;
  note: string | null;
}

/**
 * Which networks each room holds, for a site surveyed only to room level (no
 * racks yet). A room-level presence counts with its own confidence; a room
 * reached only through devices takes the weakest in-service member's
 * confidence. Unconfirmed (unknown) device members never place a network in a
 * room, matching `networkFootprint()`.
 */
export function roomNetworkIndex(plant: RackPlant): Map<string, RoomNetworkPresence[]> {
  const rank: Record<string, number> = { verified: 0, deduced: 1, contested: 2, unknown: 3 };
  const out = new Map<string, RoomNetworkPresence[]>();
  for (const network of plant.networks) {
    const byRoom = new Map<string, RoomNetworkPresence>();
    for (const m of plant.membersByNetwork.get(network.id) ?? []) {
      if (m.room_id) {
        byRoom.set(m.room_id, { network, via: 'presence', confidence: m.confidence, note: m.note });
        continue;
      }
      if (m.confidence === 'unknown') continue;
      const unit = m.unit_id ? plant.unitById.get(m.unit_id) : undefined;
      const roomId = unit ? plant.rackById.get(unit.rack_id)?.room_id : undefined;
      if (!roomId) continue;
      const prev = byRoom.get(roomId);
      if (prev?.via === 'presence') continue;
      if (!prev || rank[m.confidence] > rank[prev.confidence]) {
        byRoom.set(roomId, { network, via: 'devices', confidence: m.confidence, note: null });
      }
    }
    for (const [roomId, p] of byRoom) push(out, roomId, p);
  }
  return out;
}

export const CONFIDENCE_LABEL: Record<Confidence, string> = {
  verified: 'Verified',
  deduced: 'Deduced',
  contested: 'Contested',
  unknown: 'Unknown',
  vacant: 'Vacant',
};

export const MEDIUM_LABEL: Record<LinkMedium, string> = {
  fiber_mm: 'Multimode fiber',
  fiber_sm: 'Single-mode fiber',
  fiber: 'Fiber',
  copper: 'Copper',
  logical: 'Logical',
  other: 'Other',
};
